import React, { FC, useEffect, useRef, useState } from 'react';
import { ArrowTopRightOnSquareIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useCookies } from 'next-client-cookies';
import { useTheme } from 'next-themes';
import { Wishlist } from 'shared/types/wishlist';
import { useTranslations } from 'use-intl';
import Button from 'components/commercetools-ui/atoms/button';
import Drawer from 'components/commercetools-ui/atoms/drawer';
import ImageLink from 'components/commercetools-ui/atoms/ImageLink';
import Link from 'components/commercetools-ui/atoms/link';
import { TabsPath } from 'components/commercetools-ui/organisms/account/types/types';
import ModalPopUp from 'components/commercetools-ui/organisms/modal-pop-up';
import { CloseIcon, MenuIcon } from 'components/icons';
import { trackMenuNavigation } from 'helpers/analytics/ga4Events';
import { DARK, PRESSED_ENTER } from 'helpers/constants/events';
import { hasFavorites, hasProject } from 'helpers/has-favorites';
import { mapCountry } from 'helpers/utils/mapCountry';
import { cleanString, showBadge } from 'helpers/utils/util';
import { useRouter } from 'i18n/routing';
import { useAccount } from 'frontastic';
import { useFeatures } from 'providers/feature-flag';
import { useMessagePort } from 'providers/message-channel';
import CountrySwitchModal from '../../utility-section/components/country-switch-modal';
import { HeaderContentProps } from '../../types/gmsb-header';
import TranslationDropDown from '../../utility-section/components/translation-dropdown';
import { UtilityIcons } from '../../utility-section/components/UtilityIcons';
import { useCategoryBar } from 'providers/categoryBar';


export interface Props {
  ariaLabelLogo?: string;
  contentConfig: HeaderContentProps;
  imageClassName?: string;
  wishlist?: Wishlist;
}

const HeaderNavigationMobile: FC<Props> = ({ contentConfig, ariaLabelLogo, imageClassName, wishlist }) => {
  const translate = useTranslations();
  const [showMenu, setShowMenu] = useState(false);

  const showHeaderMenu = () => {
    setShowMenu(true);

    setTimeout(() => {
      closeBtnRef.current?.focus();
    }, 0);
  };
  const hideHeaderMenu = () => {
    setShowMenu(false);
    setTimeout(() => {
      hamburgerBtnRef.current?.focus();
    }, 0);
  };

  const {
    primaryLogoLinkMobile,
    primaryLogoMobileLight,
    secondaryLogoMobileLight,
    myAccountIconLight,
    myAccountText,
    helpIconLight,
    ariaLabelHelp,
    ariaLabelProjects,
    projectIconLight,
    projectIconDark,
    helpIconDark,
    myAccountIconDark,
    ariaLabelFavourites,
    favouritesIconLight,
    favouritesIconDark,
    secondaryLogoLink,
    orderStatusMob,
    addressBookMob,
    paymentMethodsMob,
    myAccountMob,
    myPhotosLabel,
    googlePhotosLabel,
    googleIcon,
    leaveStoreLabel,
    stayOnPageLabel,
    returnGooglePhotosLabel,
    returnGooglePhotosDesc,
    helpLink,
  } = contentConfig;

  const { resolvedTheme = 'light' } = useTheme();
  const categoryBarContext = useCategoryBar();
  const categoryBarData = categoryBarContext?.categoryBarData ?? [];
  const { lineItems: wishlistLineItems = [] } = wishlist || {};
  const [isAccountVisible, setIsAccountVisible] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const hasSecondary = !!secondaryLogoMobileLight?.src;
  const router = useRouter();
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const hamburgerBtnRef = useRef<HTMLButtonElement | null>(null);
  const cookies = useCookies();
  const locale = (cookies.get('locale') as string) || 'en-US';
  const { enableMyPhotos } = useFeatures();
  const isMyPhotosEnabled = enableMyPhotos();
  const { account } = useAccount();
  const canSwitchCountry = account?.custom?.fields?.canSwitchCountry === true;
  const lang = locale?.split(/[-_]/)[0]?.toLowerCase() ?? 'en';
  const countries = mapCountry(locale.split('-')[1]);

  const localesCount = (countries?.locales ?? []).length;

  let intercomConfiguredUrl = '';

  if (locale === 'fr-ca') {
    intercomConfiguredUrl = `${helpLink}/fr-CA/`;
  } else {
    intercomConfiguredUrl = `${helpLink}/${lang}/`;
  }

  const redirectToFavorites = () => {
    trackMenuNavigation('Header Quick Links', 'Favorites', '/favorites');
    router.push('/favorites');
  };

  const redirectToMyProject = () => {
    const linkUrl = typeof secondaryLogoLink === 'string' ? secondaryLogoLink : (secondaryLogoLink?.link ?? '');
    trackMenuNavigation('Header Quick Links', 'Draft Projects', linkUrl);
    router.push('/my-project');
  };

  const { sendExitStore } = useMessagePort();
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isCountrySwitchOpen, setIsCountrySwitchOpen] = useState(false);

  const openMobileCountrySwitch = () => {
    setShowMenu(false);

    // Pushes opening the country switch to the next event loop tick 
    // to prevent the drawer from unlocking scrolling after the country locks it
    setTimeout(() => {
      setIsCountrySwitchOpen(true);
    }, 0);
  };

  const closeMobileCountrySwitch = () => {
    setIsCountrySwitchOpen(false);
    setTimeout(() => {
      hamburgerBtnRef.current?.focus();
    }, 0);
  };

  const handlePhotos = () => {
    setIsOpenModal(false);
    sendExitStore();
  };

  const handleStayOnPage = () => {
    setIsOpenModal(false);
  };

  useEffect(() => {
    if (isAccountVisible) {
      setTimeout(() => {
        closeBtnRef.current?.focus();
      }, 0);
    }
  }, [isAccountVisible]);

  return (
    <nav className="flex lg:hidden" role="navigation" aria-label="Mobile header navigation">
      <Button
        variant="ghost"
        size="fit"
        onClick={showHeaderMenu}
        title={translate('common.header-menu-open')}
        aria-label="Open mobile menu"
        data-ffid="btn-open-menu"
        ref={hamburgerBtnRef}
      >
        <MenuIcon className="size-24" />
      </Button>

      <Drawer
        isOpen={showMenu}
        direction="left"
        className="w-[calc(100%-50px)] overflow-y-auto bg-white dark:shadow-[8px_0px_20px_4px_#FFFFFF1A] dark:bg-gmsb-gray"
        onClose={hideHeaderMenu}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile menu drawer"
        data-ffid="btn-drawer"
      >
        <div
          className={`${(isAccountVisible || selectedCategory) ? 'items-center justify-between' : 'justify-end'} mb-12 mr-18 mt-14 flex cursor-pointer`}
          aria-label="Close mobile menu"
        >
          {(isAccountVisible || selectedCategory) && (
            <Button
              variant="ghost"
              data-ffid={'btn-back-account'}
              tabIndex={0}
              className="ml-4 mt-4 flex items-center"
              onClick={() => {
                if (selectedCategory?.parentCategory) {
                  setSelectedCategory(selectedCategory.parentCategory);
                } else if (selectedCategory) {
                  setSelectedCategory(null);
                } else {
                  setIsAccountVisible(false);
                }
              }}

            >
              <ChevronLeftIcon className="mr-4 w-22" data-ffid={'icn-back-account'} />
              <span data-ffid="lbl-back-account" className="text-16 font-bold">
                {'Back'}
              </span>
            </Button>
          )}
          <button
            onClick={hideHeaderMenu}
            ref={closeBtnRef}
            onKeyDown={(e) => e.key === PRESSED_ENTER && hideHeaderMenu()}
            className="rounded-[34px] bg-[#F5F5F5] p-12 dark:bg-[#5C5C5C]"
            data-ffid="btn-close-menu"
          >
            <CloseIcon className="size-18" />
          </button>
        </div>

        <div className="mx-24 mt-31 flex flex-col">
          <div className="mb-23 flex h-30 max-w-315 items-center justify-center -ml-20">
            {[primaryLogoMobileLight, ...(secondaryLogoMobileLight ? [secondaryLogoMobileLight] : [])].map(
              (media, index) => {
                return (
                  <ImageLink
                    key={`${media?.alt}-${index}`}
                    media={media}
                    link={index == 0 ? primaryLogoLinkMobile : secondaryLogoLink}
                    className={imageClassName}
                    title={ariaLabelLogo}
                    ffid={index == 0 ? `header-primary-logo-drawer` : `header-secondary-logo-drawer`}
                    marginRight={index === 0 ? '10px' : undefined}
                    hasSecondary={hasSecondary}
                    imageClassNames={'object-scale-down'}
                    onClick={() => {
                      hideHeaderMenu();
                    }}
                    cropType="scale"
                    unoptimized
                  />
                );
              },
            )}
          </div>

          {selectedCategory ? (
            <>
              <Link
                link={
                  selectedCategory.categoryLinks ||
                  selectedCategory.subcategoryUrls ||
                  selectedCategory.subSubCategoryLink
                }
                data-ffid={
                  selectedCategory.categoryNameFFID
                    ? `lnk-cat-menu-${selectedCategory.categoryNameFFID}`
                    : `lnk-subcat-sub-${selectedCategory.subcategoryNameFFID}`
                }
                onClick={hideHeaderMenu}
                className="mt-12 text-[14px] font-bold text-[#018463] ml-32"
              >
                <span
                  data-ffid={
                    selectedCategory.categoryNameFFID
                      ? `lbl-cat-menu-item-${selectedCategory.categoryNameFFID}`
                      : `lbl-subcat-label-${selectedCategory.subcategoryNameFFID}`
                  }
                >
                  {
                    selectedCategory.categoryName ||
                    selectedCategory.subcategoryName ||
                    selectedCategory.subSubCategoryName
                  }
                </span>
              </Link>
              <ul className="mt-12 flex list-none flex-col text-14">
                {(selectedCategory?.subcategoryOptions ?? []).map((item: any) => {

                  const hasChildren =
                    Boolean(item?.subcategoryOptions?.length) ||
                    Boolean(item?.subSubCategoryOptions?.length);

                  const badge = item?.badgeConfigs?.[0];
                  const badgeVisible =
                    badge &&
                    showBadge(badge?.badgeAvailableFrom, badge?.badgeExpiresOn);

                  return hasChildren ? (
                    <button
                      data-ffid={`lnk-subcat-sub-${item.subcategoryNameFFID}`}
                      key={
                        item.categoryName ||
                        item.subcategoryName ||
                        item.subsubcategoryName
                      }
                      type="button"
                      className="relative flex items-center justify-between py-8 text-14 ml-32"
                      onClick={() =>
                        setSelectedCategory({
                          ...item,
                          parentCategory: selectedCategory,
                          subcategoryOptions:
                            item.subcategoryOptions || item.subSubCategoryOptions || [],
                        })
                      }
                    >
                      <div className="flex items-center gap-8">
                        <span
                          className="text-primary dark:text-white"
                          data-ffid={`lbl-subcat-label-${item.subcategoryNameFFID}`}
                        >
                          {item.categoryName ||
                            item.subcategoryName ||
                            item.subsubcategoryName}
                        </span>

                        {badgeVisible && (
                          <span
                            className="inline-block truncate rounded-[34px] border-none px-8 leading-5"
                            data-ffid={`lbl-badge-${cleanString(badge?.badgeText ?? '')}`}
                            style={{
                              fontSize: Number(badge.badgeTextSize),
                              color: badge.badgeTextColor,
                              backgroundColor: badge.badgeBgColor,
                              fontWeight: badge.badgeFontWeight,
                            }}
                          >
                            {badge.badgeText}
                          </span>
                        )}
                      </div>

                      {hasChildren && (
                        <ChevronRightIcon
                          className="w-20 text-[#333333] dark:text-white"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  ) : (
                    <Link
                      key={
                        item.categoryName ||
                        item.subcategoryName ||
                        item.subSubCategoryName ||
                        item.subsubcategoryName
                      }
                      link={
                        item.categoryLinks ||
                        item.subcategoryUrls ||
                        item.subSubCategoryLink ||
                        item.ctaLink
                      }
                      data-ffid={`mnu-subsubcat-item-${item.subSubcategoryNameFFID}`}
                      onClick={hideHeaderMenu}
                      className="relative flex items-center justify-between py-8 text-14 ml-32"
                    >
                      <div className="flex items-center gap-8">
                        <span
                          data-ffid={`lbl-subsubcat-label-${item.subSubcategoryNameFFID}`}
                        >
                          {item.categoryName ||
                            item.subcategoryName ||
                            item.subSubCategoryName ||
                            item.subsubcategoryName ||
                            item.ctaText}
                        </span>

                        {badgeVisible && (
                          <span
                            className="inline-block truncate rounded-[34px] border-none px-8 leading-5"
                            data-ffid={`lbl-badge-${cleanString(badge?.badgeText ?? '')}`}
                            style={{
                              fontSize: Number(badge.badgeTextSize),
                              color: badge.badgeTextColor,
                              backgroundColor: badge.badgeBgColor,
                              fontWeight: badge.badgeFontWeight,
                            }}
                          >
                            {badge.badgeText}
                          </span>
                        )}
                      </div>
                    </Link>
                  );
                })}

              </ul>
            </>
          ) : isAccountVisible ? (
            <ul className="mt-12 flex list-none flex-col gap-12 text-14">
              <div
                data-ffid="lnk-myaccount-sec"
                className="category-barfilter-color flex cursor-default items-center gap-8"
              >
                <UtilityIcons
                  isDark={resolvedTheme == DARK}
                  darkIcon={myAccountIconDark}
                  lightIcon={myAccountIconLight}
                  isSpan={true}
                  ariaLabel="My Account"
                  ffid={`toggle-myaccount-section-mob`}
                />
                <span className="font-bold text-primary hover:font-bold dark:text-white" data-ffid="lbl-myaccount-sec">
                  {myAccountText}
                </span>
              </div>
              <Link
                data-ffid={'lnk-account-dashboard-mob'}
                link={`/account?hash=${TabsPath.MY_ACCOUNT}`}
                onKeyDown={(e) => e.key === PRESSED_ENTER && hideHeaderMenu()}
                className="ml-32 flex items-center gap-8"
                onClick={() => {
                  trackMenuNavigation('Header My Account', 'Account Dashboard', `/account?hash=${TabsPath.MY_ACCOUNT}`);
                  hideHeaderMenu();
                }}
              >
                {myAccountMob}
              </Link>
              {isMyPhotosEnabled && (
                <Link
                  data-ffid={'lnk-myphotos-mob'}
                  className="ml-32 flex items-center gap-8"
                  link={`/account?hash=${TabsPath.MY_PHOTOS}`}
                  onKeyDown={(e) => e.key === PRESSED_ENTER && hideHeaderMenu()}
                  onClick={() => {
                    trackMenuNavigation('Header My Account', 'My Photos', `/account?hash=${TabsPath.MY_PHOTOS}`);
                    hideHeaderMenu();
                  }}
                >
                  {myPhotosLabel}
                </Link>
              )}
              <Link
                data-ffid={'lnk-oerderstatus-mob'}
                className="ml-32 flex items-center gap-8"
                link={`/account?hash=${TabsPath.ORDERS_STATUS}`}
                onKeyDown={(e) => e.key === PRESSED_ENTER && hideHeaderMenu()}
                onClick={() => {
                  trackMenuNavigation('Header My Account', 'Order History', `/account?hash=${TabsPath.ORDERS_STATUS}`);
                  hideHeaderMenu();
                }}
              >
                {orderStatusMob}
              </Link>
              <Link
                data-ffid={'lnk-address-mob'}
                link={`/account?hash=${TabsPath.ADDRESS_BOOK}`}
                onKeyDown={(e) => e.key === PRESSED_ENTER && hideHeaderMenu()}
                className="ml-32 flex items-center gap-8"
                onClick={() => {
                  trackMenuNavigation('Header My Account', 'Address Book', `/account?hash=${TabsPath.ADDRESS_BOOK}`);
                  hideHeaderMenu();
                }}
              >
                {addressBookMob}
              </Link>
              <Link
                data-ffid={'lnk-paymentmtd-mob'}
                link={`/account?hash=${TabsPath.PAYMENT_METHODS}`}
                onKeyDown={(e) => e.key === PRESSED_ENTER && hideHeaderMenu()}
                className="ml-32 flex items-center gap-8"
                onClick={() => {
                  trackMenuNavigation(
                    'Header My Account',
                    'Payment Methods',
                    `/account?hash=${TabsPath.PAYMENT_METHODS}`,
                  );
                  hideHeaderMenu();
                }}
              >
                {paymentMethodsMob}
              </Link>
              {canSwitchCountry && (
                <button
                  type="button"
                  data-ffid="btn-switch-country-mob"
                  className="ml-32 flex items-center gap-8 text-left"
                  onClick={openMobileCountrySwitch}
                >
                  {translate('customer-support.country-switch-title')}
                </button>
              )}
              <button
                className="border-1 mt-8 flex border-t border-[#EBEBEB] pt-8 text-14 text-[#333333]"
                onClick={() => setIsOpenModal(true)}
                type="button"
              >
                <img
                  src={googleIcon?.media?.file}
                  alt="google-photo-image"
                  className="mr-8 w-20"
                  data-ffid="google-photo-image"
                />
                {googlePhotosLabel}
              </button>
            </ul>
          ) : (
            <>
              {categoryBarData?.map((category: any) => (
                <button
                  key={category.categoryName}
                  className="relative flex items-center justify-between border-y border-solid border-[#EBEBEB] py-8 text-14 dark:border-[#5c5c5c]"
                  type="button"
                  onClick={() => {
                    if (category?.subcategoryOptions?.length > 0) {
                      setSelectedCategory(category);
                    } else {
                      router.push(
                        typeof category.categoryLinks === 'string'
                          ? category.categoryLinks
                          : (category.categoryLinks?.link ?? '')
                      );
                      hideHeaderMenu();
                    }
                  }}
                >
                  <span className="text-primary dark:text-white">
                    {category.categoryName}
                  </span>

                  {category?.subcategoryOptions?.length > 0 && (
                    <ChevronRightIcon
                      className="w-20 text-[#333333] dark:text-white"
                      aria-hidden="true"
                    />
                  )}
                </button>
              ))}
              <button
                className={`relative flex items-center justify-between border-y border-solid border-[#EBEBEB] py-8 text-14 dark:border-[#5c5c5c]`}
                aria-label={`Toggle ${ariaLabelFavourites || 'favourites'} section`}
                onKeyDown={(e) => e.key === PRESSED_ENTER && null}
                onClick={() => redirectToFavorites()}
                data-ffid="btn-favourites-section"
                type='button'
              >
                <div className="category-barfilter-color flex items-center gap-8">
                  <div className="cart-icon">
                    {wishlistLineItems.length > 0 && hasFavorites(wishlistLineItems) && (
                      <span className="cart-dot" data-testid="wishlist-dot" data-ffid="wishlist-dot"></span>
                    )}
                    <UtilityIcons
                      isDark={resolvedTheme == DARK}
                      darkIcon={favouritesIconDark}
                      lightIcon={favouritesIconLight}
                      ariaLabel={`Toggle ${ariaLabelFavourites || 'favourites'} section`}
                      ffid={`toggle-favourites-section`}
                      isSpan
                    />
                  </div>

                  <span
                    className="text-primary hover:font-bold dark:text-white"
                    data-ffid={`lbl-${cleanString(ariaLabelFavourites ?? '')}`}
                  >
                    {ariaLabelFavourites}
                  </span>
                </div>
              </button>
              <button
                className={`relative flex items-center justify-between border-y border-solid border-[#EBEBEB] py-8 text-14 dark:border-[#5c5c5c]`}
                aria-label={`Toggle ${ariaLabelProjects || 'Projects'} section`}
                onKeyDown={(e) => e.key === PRESSED_ENTER && null}
                onClick={() => redirectToMyProject()}
                data-ffid="btn-project-section"
                type='button'
              >
                <div className="category-barfilter-color flex items-center gap-8">
                  <div className="cart-icon">
                    {wishlistLineItems.length > 0 && hasProject(wishlistLineItems) && (
                      <span className="cart-dot" data-testid="wishlist-dot" data-ffid="wishlist-dot"></span>
                    )}

                    <UtilityIcons
                      isDark={resolvedTheme == DARK}
                      darkIcon={projectIconDark}
                      lightIcon={projectIconLight}
                      ariaLabel={`Toggle ${ariaLabelProjects || 'Projects'} section`}
                      ffid={`toggle-projects-section`}
                      isSpan
                    />
                  </div>

                  <span
                    className="text-primary hover:font-bold dark:text-white"
                    data-ffid={`lbl-${cleanString(ariaLabelProjects ?? '')}`}
                  >
                    {ariaLabelProjects}
                  </span>
                </div>
              </button>
              {localesCount > 1 && <TranslationDropDown contentConfig={contentConfig} />}

              <a
                href={intercomConfiguredUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`relative flex items-center justify-between border-y border-solid border-[#EBEBEB] py-8 text-14 dark:border-[#5c5c5c]`}
                aria-label={`Toggle ${ariaLabelHelp || 'Help'} section`}
                data-ffid="btn-help-section"
                onClick={() => {
                  const linkUrl = typeof helpLink === 'string' ? helpLink : (helpLink?.link ?? '');
                  trackMenuNavigation('Header Quick Links', 'Help', `/${linkUrl}`);
                }}
              >
                <div className="category-barfilter-color flex items-center gap-8">
                  <UtilityIcons
                    isDark={resolvedTheme == DARK}
                    darkIcon={helpIconDark}
                    lightIcon={helpIconLight}
                    ariaLabel={`Toggle ${ariaLabelHelp || 'Help'} section`}
                    ffid={`toggle-help-section`}
                    isSpan
                  />
                  <span className="text-primary hover:font-bold dark:text-white" data-ffid="lbl-help">
                    {ariaLabelHelp}
                    <ArrowTopRightOnSquareIcon className="inline ml-2 pb-4 size-20" aria-hidden="true" />
                  </span>
                </div>
              </a>
              <button
                className={`relative flex items-center justify-between border-y border-solid border-[#EBEBEB] py-8 text-14 dark:border-[#5c5c5c]`}
                type="button"
                tabIndex={0}
                onClick={() => setIsAccountVisible(true)}
                aria-label={`Toggle ${myAccountText || 'My Account'} section`}
                onKeyDown={(e) => e.key === PRESSED_ENTER && null}
                data-ffid="btn-my-account-section"
              >
                <div className="category-barfilter-color flex items-center gap-8">
                  <UtilityIcons
                    isDark={resolvedTheme == DARK}
                    darkIcon={myAccountIconDark}
                    lightIcon={myAccountIconLight}
                    ariaLabel={`Toggle ${myAccountText || 'My Account'} section`}
                    ffid={`toggle-myaccount-section`}
                    isSpan
                  />
                  <span className="text-primary hover:font-bold dark:text-white" data-ffid="lbl-myaccount">
                    {myAccountText}
                  </span>
                </div>
                <ChevronRightIcon
                  data-ffid={`right-icon-myaccount`}
                  className={`w-20 text-[#333333] transition-transform duration-300 dark:text-white ${false ? 'rotate-180' : 'rotate-0'}`}
                  aria-hidden="true"
                />
              </button>
            </>
          )}
        </div>
      </Drawer>
      <ModalPopUp
        isOpen={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        title={returnGooglePhotosLabel}
        messageDescription={returnGooglePhotosDesc}
        showCancelButton
        cancelText={leaveStoreLabel}
        onCancel={() => handlePhotos()}
        actionText={stayOnPageLabel}
        onAction={() => {
          handleStayOnPage();
        }}
      />
      <CountrySwitchModal
        isOpen={isCountrySwitchOpen}
        onClose={closeMobileCountrySwitch}
      />
    </nav >
  );
};

export default HeaderNavigationMobile;
